import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { CheckCircle2 } from 'lucide-react';
interface SlideBusinessModelProps {
  onContinue: () => void;
}
const CUSTOMERS = [{
  name: 'GSK',
  stage: 'Early Stage III',
  detail: '5 products · Nigeria & Kenya · +7 Gulf countries next',
  highlight: true
}, {
  name: 'Solventum*',
  stage: 'Late Stage II',
  detail: '3 products · MedSurge + Dental · UK, Germany, Nordics & South Africa',
  highlight: false
}, {
  name: 'Leo Pharma',
  stage: 'Stage II',
  detail: '5 products · UK, Brazil, Poland & Greece',
  highlight: false
}, {
  name: 'Roche Pharmaceutical',
  stage: 'Early Stage II',
  detail: '3 products · China, Canada, Italy & Germany',
  highlight: false
}] as const;
export function SlideBusinessModel({
  onContinue
}: SlideBusinessModelProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 200);
    const t2 = setTimeout(() => setStage(2), 800);
    return () => {
      ;
      [t1, t2].forEach(clearTimeout);
    };
  }, []);
  return <div className="h-full w-full bg-white flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-[1200px] mx-auto" data-id="element-170">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} data-id="element-171">
        <p className="text-[#6C2BD9] text-xs md:text-sm font-bold uppercase tracking-[0.2em]" data-id="element-172">
          The business model
        </p>
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-slate-900 tracking-tight leading-tight mt-3" data-id="element-173">
          We want to run the campaign calendar for{' '}
          <span className="text-[#6C2BD9]" data-id="element-174">legacy portfolios</span>
        </h2>
        <p className="text-slate-600 text-lg md:text-xl mt-4 max-w-2xl leading-relaxed" data-id="element-175">
          One contract per account. 3–4 campaigns per product, per year, across
          multiple countries.
        </p>
      </motion.div>

      {/* Customers */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} className="flex-1 flex flex-col justify-center mt-10" data-id="element-176">
        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-4" data-id="element-177">
          Already running this model
        </p>
        <div className="space-y-2" data-id="element-178">
          {CUSTOMERS.map((c, idx) => <motion.div key={c.name} initial={{
          opacity: 0,
          x: -16
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          x: stage >= 2 ? 0 : -16
        }} transition={{
          duration: 0.4,
          delay: 0.1 + idx * 0.08
        }} className="flex items-center gap-4 py-3 border-b border-slate-200 last:border-b-0" data-id="element-179">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${c.highlight ? 'bg-[#6C2BD9] text-truewhite' : 'bg-[#F5F0FF] text-[#6C2BD9]'}`} data-id="element-180">
                <CheckCircle2 className="w-4 h-4" data-id="element-181" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-182">
                <p className="text-base md:text-lg font-bold text-slate-900" data-id="element-183">
                  {c.name}
                </p>
                <p className="text-sm text-slate-500" data-id="element-184">{c.detail}</p>
              </div>
              <span className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${c.highlight ? 'bg-[#6C2BD9] text-truewhite' : 'bg-slate-100 text-slate-600'}`} data-id="element-185">
                {c.stage}
              </span>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: stage >= 2 ? 1 : 0,
        y: stage >= 2 ? 0 : 10
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="mt-6 rounded-2xl bg-[#6C2BD9] px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-186">
          <p className="text-[10px] uppercase tracking-[0.25em] text-truewhite/80 font-bold" data-id="element-187">
            Potential per company
          </p>
          <p className="text-base md:text-lg font-bold text-truewhite leading-snug" data-id="element-188">
            100+ countries · 10× the brands
          </p>
        </motion.div>

        <p className="text-[11px] text-slate-400 italic mt-3" data-id="element-189">
          * Solventum is the former 3M Health Care, with an annual revenue of
          €8B+.
        </p>
      </motion.div>
    </div>;
}